import { BlockPageApp } from '@omni/blocks';
import { KitButton, KitText } from '@omni/kit/components';
import KitIcon from '@omni/kit/components/KitIcon';
import * as Icons from '@omni/kit/img';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { NativeTopBarStyle } from '@omni/kit/utilities/NativeHelpers';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useState } from 'react';
import {
  Animated,
  Dimensions,
  SafeAreaView,
  StyleSheet,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';

import { appKeySelector } from '../../shared/redux/selectors';
import {
  retrieveIntroPresentedFromDevice,
  saveIntroPresentedToDevice,
  saveOmniVersion,
} from '../../utilities/mobileStorage';

const whatsNew = require('@omni/blocks/templates/examples/whatsNew.json');

const debug = require('debug')('tca:chat:screen:IntroScreen');

//******************************************************************************
// Types
//******************************************************************************
interface IIntroProps {
  navigation: StackNavigationProp<any, any>;
}

//******************************************************************************
// Component
//******************************************************************************

export default function IntroScreen({ navigation }: IIntroProps): JSX.Element {
  const [presentIntro, setPresentIntro] = useState<boolean | undefined>(
    undefined
  );

  const _onButtonPress = () => {
    saveIntroPresentedToDevice(true);
    saveOmniVersion();
    navigation.goBack();
  };

  useEffect(() => {
    retrieveIntroPresentedFromDevice().then((presented) => {
      setPresentIntro(!presented);
    });
  }, []);

  return (
    <SafeAreaView style={styles.container}>
      {/* Waits for retrieveIntroPresentedFromDevice to finish so presentIntro will not be undefined */}
      {presentIntro === true && <IntroBlock onContinuePress={_onButtonPress} />}
      {presentIntro === false && (
        <WhatsNewBlock onContinuePress={_onButtonPress} />
      )}
    </SafeAreaView>
  );
}

const IntroBlock = ({ onContinuePress }: { onContinuePress: () => void }) => {
  const { height } = Dimensions.get('window');
  const [opacity] = useState(new Animated.Value(0));

  Animated.spring(opacity, {
    toValue: 1,
    delay: 500,
    useNativeDriver: true,
  }).start();

  return (
    <View
      style={{
        padding: Spacing.xxl,
        marginTop: Spacing.xl,
        height: '100%',
      }}
    >
      <>
        <Animated.View
          style={{
            flex: 0.9,
            justifyContent: 'flex-end',
            alignItems: 'center',
            opacity: opacity,
            transform: [
              {
                translateY: opacity.interpolate({
                  inputRange: [0, 1],
                  outputRange: [50, 0],
                }),
              },
            ],
          }}
        >
          <KitIcon name='messaging' />
        </Animated.View>
        <View
          style={{
            flex: 1,
            alignContent: 'center',
            alignItems: 'center',
            marginTop: height > 600 ? Spacing.xxl : Spacing.m,
            marginBottom: Spacing.xxl,
          }}
        >
          <View style={{ maxWidth: 300 }}>
            <KitText
              style={styles.titleText}
              fontSize={26}
              extraBold
              lineHeight={30}
              center
              color={colorForScheme({ default: Colors.N800 })}
            >
              Real-time messaging with your crew!
            </KitText>
          </View>
          <KitText style={styles.introText}>
            Say goodbye to group text and email to take your communication to a
            deeper level with the people in your circle, and be sure no one is
            missing out on the conversation.
          </KitText>
        </View>
        <KitButton title='Continue' onPress={onContinuePress} />
      </>
    </View>
  );
};

const WhatsNewBlock = ({
  onContinuePress,
}: {
  onContinuePress: () => void;
}) => {
  const appKey = useSelector(appKeySelector);

  return (
    <>
      <BlockPageApp
        appKey={appKey}
        data={whatsNew}
        theme='light'
        topBarStyle={NativeTopBarStyle.Default}
      />
      <KitButton
        title='Continue'
        onPress={onContinuePress}
        style={{ marginHorizontal: Spacing.xl, marginVertical: Spacing.xl }}
      />
    </>
  );
};

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: colorForScheme({ default: Colors.N0 }),
  },
  titleText: {
    marginBottom: Spacing.m,
  },
  introText: {
    justifyContent: 'center',
    textAlign: 'center',
  },
});
